// src/pages/shop/ShopAiAssistant.jsx
//
// Чат с ИИ-ассистентом аналитики + история чатов.
// Read-only: ассистент отвечает по реальным цифрам и советует, действий не выполняет.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiAssistant as assistantApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import Icon from '../../components/Icon';

const GRAD = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';

export default function ShopAiAssistant() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();

  const [status, setStatus]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId]   = useState(null);
  const [input, setInput]     = useState('');
  const [sending, setSending] = useState(false);

  const [view, setView]       = useState('chat');   // 'chat' | 'history'
  const [chats, setChats]     = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingChat, setLoadingChat]   = useState(false);

  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  const suggestions = [
    t('shopAiAssistant.s1'),
    t('shopAiAssistant.s2'),
    t('shopAiAssistant.s3'),
  ];

  const loadStatus = useCallback(() => {
    assistantApi.status().then(setStatus).catch(() => setStatus(null));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, view]);

  const limit = status?.limit;
  const used  = status?.used || 0;
  const reached = limit != null && used >= limit;

  async function sendMessage(text) {
    const message = String(text ?? input).trim();
    if (!message || sending) return;
    if (reached) { show(t('shopAiAssistant.limitReached')); return; }

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setSending(true);

    try {
      const res = await assistantApi.chat({ message, chatId });
      if (res.chatId) setChatId(res.chatId);
      setMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
      setStatus(s => s ? { ...s, used: (res.used ?? (s.used || 0) + 1) } : s);
    } catch (e) {
      const key = e?.serverError || e?.message || '';
      let msg = t('shopAiAssistant.error');
      if (/limit/.test(key))            msg = t('shopAiAssistant.limitReached');
      else if (/disabled/.test(key))    msg = t('shopAiAssistant.disabled');
      else if (/no_api_key/.test(key))  msg = t('shopAiAssistant.notConfigured');
      show(msg);
      setInput(message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }

  function newChat() {
    setChatId(null);
    setMessages([]);
    setInput('');
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function openHistory() {
    setView('history');
    setLoadingChats(true);
    try {
      const res = await assistantApi.chats(50);
      setChats(res.items || []);
    } catch {
      show(t('shopAiAssistant.error'));
    } finally {
      setLoadingChats(false);
    }
  }

  async function openChat(id) {
    setLoadingChat(true);
    setView('chat');
    try {
      const res = await assistantApi.chatMessages(id);
      setMessages((res.messages || []).map(m => ({ role: m.role, content: m.content })));
      setChatId(id);
    } catch {
      show(t('shopAiAssistant.error'));
      setView('history');
    } finally {
      setLoadingChat(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  function fmtTime(ts) { try { return new Date(ts).toLocaleString(); } catch { return ''; } }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100dvh', padding: 0 }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px 12px' }}>
        <button className="back-btn" onClick={() => view === 'history' ? setView('chat') : navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="sparkles" size={18} />
            {view === 'history' ? t('shopAiAssistant.historyTitle') : t('shopAiAssistant.title')}
          </h1>
        </div>
        {view === 'chat' && (
          <>
            <button className="back-btn" onClick={openHistory} title={t('shopAiAssistant.history')}>
              <Icon name="list" size={18} />
            </button>
            <button className="back-btn" onClick={newChat} title={t('shopAiAssistant.newChat')}>
              <Icon name="plus" size={18} />
            </button>
          </>
        )}
      </div>

      {view === 'history' ? (
        /* ── История чатов ── */
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 20px' }}>
          {loadingChats ? (
            <p style={{ textAlign: 'center', color: 'var(--fg-muted)', marginTop: 24, fontSize: 14 }}>
              {t('shopAiAssistant.thinking')}
            </p>
          ) : chats.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--fg-muted)', marginTop: 32, fontSize: 14 }}>
              {t('shopAiAssistant.noChats')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {chats.map(c => (
                <button key={c.id} onClick={() => openChat(c.id)} style={{
                  padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)', display: 'flex', flexDirection: 'column', gap: 3,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{fmtTime(c.createdAt)}</span>
                  {c.title && (
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.title}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ── Чат ── */
        <>
          {limit != null && (
            <div style={{ padding: '0 20px 8px' }}>
              <span style={{ fontSize: 11, color: reached ? 'var(--danger)' : 'var(--fg-faint)', fontWeight: 600 }}>
                {t('shopAiAssistant.used')}: {used} / {limit}
                {status?.resetAt && ` · ${t('shopAi.resetAt')} ${new Date(status.resetAt).toLocaleDateString()}`}
              </span>
            </div>
          )}

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>
            {loadingChat ? (
              <p style={{ textAlign: 'center', color: 'var(--fg-muted)', marginTop: 24, fontSize: 14 }}>
                {t('shopAiAssistant.thinking')}
              </p>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', marginTop: 32 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px', background: GRAD,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                }}>
                  <Icon name="sparkles" size={28} />
                </div>
                <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{t('shopAiAssistant.emptyTitle')}</p>
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 20, lineHeight: 1.5 }}>
                  {t('shopAiAssistant.emptyHint')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => sendMessage(s)} style={{
                      padding: '12px 16px', textAlign: 'left', background: 'var(--surface)',
                      border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                      fontSize: 14, color: 'var(--fg)', cursor: 'pointer',
                    }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                  <div style={{
                    maxWidth: '82%', padding: '10px 14px', borderRadius: 14,
                    fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    ...(m.role === 'user'
                      ? { background: GRAD, color: '#fff', borderBottomRightRadius: 4 }
                      : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--fg)', borderBottomLeftRadius: 4 }),
                  }}>
                    {m.content}
                  </div>
                </div>
              ))
            )}

            {sending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                <div style={{
                  padding: '10px 14px', borderRadius: 14, borderBottomLeftRadius: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  fontSize: 14, color: 'var(--fg-muted)',
                }}>
                  {t('shopAiAssistant.thinking')}
                </div>
              </div>
            )}
          </div>

          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-end',
            padding: '10px 16px calc(10px + env(safe-area-inset-bottom))',
            borderTop: '1px solid var(--border)', background: 'var(--bg)',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={t('shopAiAssistant.placeholder')}
              disabled={sending}
              style={{
                flex: 1, resize: 'none', maxHeight: 120, padding: '12px 14px',
                fontSize: 14, lineHeight: 1.4, border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'inherit',
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              style={{
                width: 44, height: 44, flexShrink: 0, border: 'none', borderRadius: 'var(--r-md)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                background: (sending || !input.trim()) ? 'var(--bg-subtle)' : GRAD,
                opacity: (sending || !input.trim()) ? 0.6 : 1,
              }}
            >
              <Icon name="arrowRight" size={20} />
            </button>
          </div>
        </>
      )}

      {ToastView}
    </div>
  );
}
